<template>
  <div class="flex flex-col">
    <div class="divide-y divide-gray-950 bg-gray-900 rounded-lg max-h-[300px] overflow-y-auto">
      <table class="w-full divide-y divide-gray-950">
        <thead class="sticky top-0 bg-gray-900">
          <tr>
            <th class="text-text-primary font-medium text-left px-4 py-3">{{ title }}</th>
            <th class="text-text-secondary font-medium tracking-[1.5px] text-xs uppercase text-right px-4 py-3 w-1/4">{{ $t('coach.stat_comparison.current_average') }}</th>
            <th class="text-text-secondary font-medium tracking-[1.5px] text-xs uppercase text-right px-4 py-3 w-1/4">{{ $t('coach.stat_comparison.target_average') }}</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-950 w-full">
          <tr v-for="(stat, index) in stats" :key="index" class="p-4 w-full gap-2 items-center">
            <template v-if="hasWeaponFilter && stat.perWeapon === false">
              <td class="font-medium text-gray-600 text-left p-4 flex items-center gap-2">
                {{ stat.label }}
                <span class="text-xs text-gray-600 ml-auto whitespace-nowrap">{{ $t('coach.stat_comparison.not_per_weapon') }}</span>
              </td>
              <td class="text-right p-4 w-1/4 text-gray-700">&mdash;</td>
              <td class="text-right p-4 w-1/4 text-gray-700">&mdash;</td>
            </template>
            <template v-else>
              <td class="font-medium text-text-secondary text-left p-4 flex items-center gap-2">
                {{ stat.label }}
                <Info v-if="stat.description" :title="stat.label" :description="stat.description">
                  <template v-if="rowBreakdown(stat)" #extra>
                    <StatBreakdownContent :breakdown="rowBreakdown(stat)" />
                  </template>
                </Info>
              </td>
              <td class="font-medium text-right p-4 w-1/4" :class="getStatColor(stat?.stat?.deviations_away)">
                <StatBreakdown v-if="statsMap && stat.key && hasRealValue(stat)" :stat-key="stat.key" :source="source" :values="statsMap">
                  {{ formatStatValue(stat?.stat?.players_value, stat?.stat?.unit_type) }}
                </StatBreakdown>
                <template v-else>
                  {{ formatStatValue(stat?.stat?.players_value, stat?.stat?.unit_type) }}
                </template>
              </td>
              <td class="text-right p-4 w-1/4" :class="hasRankAverage(stat?.stat?.rating_range_average_value) ? 'text-text-primary' : 'text-gray-700'">
                {{ formatPeerValue(stat?.stat?.rating_range_average_value, stat?.stat?.unit_type) }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { formatDataString } from '../../utils/formatters';
import { hasRankAverage } from '../../utils/stat_comparison';
import Info from '../General/Info.vue';
import StatBreakdown from './StatBreakdown.vue';
import StatBreakdownContent from './StatBreakdownContent.vue';
import { fetchStatDefinitions, resolveBreakdown } from '../../composables/useStatBreakdown.js';

const props = defineProps({
  title: { type: String, required: true },
  stats: { type: Array, required: true },
  hasWeaponFilter: { type: Boolean, default: false },
  // Whole stats map, so the breakdown popover can resolve a recipe's operands
  statsMap: { type: Object, default: null },
  source: { type: String, default: 'profile' },
});

fetchStatDefinitions().catch(() => {});

// The Info popover carries the breakdown as a second block (spec §4).
function rowBreakdown(stat) {
  if (!props.statsMap || !stat.key || !hasRealValue(stat)) return null;
  return resolveBreakdown(stat.key, { source: props.source, values: props.statsMap });
}

// Unit types where 0 means "no data" rather than a real value
const ZERO_MEANS_NO_DATA = new Set(['seconds', 'time', 'degrees']);

function formatStatValue(value, unitType) {
  if (value == null) return '\u2014';
  if (value === 0 && ZERO_MEANS_NO_DATA.has(unitType)) return '\u2014';
  const formatted = formatDataString(value, unitType);
  if (formatted === '' || String(formatted).includes('NaN')) return '\u2014';
  return formatted;
}

// Whatever renders as an em dash means "no data", so it gets no breakdown either;
// a genuine 0 (no flashes thrown) keeps one. Derived from the formatter so the
// two can't drift apart.
function hasRealValue(stat) {
  return formatStatValue(stat?.stat?.players_value, stat?.stat?.unit_type) !== '\u2014';
}

// The coach sends 0 for "no peer row in this bracket", so the target column
// can't take the player column's genuine-zero handling.
function formatPeerValue(value, unitType) {
  if (!hasRankAverage(value)) return '\u2014';
  return formatStatValue(value, unitType);
}

function getStatColor(deviation) {
  if (deviation == null) return 'text-text-primary';
  switch (true) {
    case deviation >= 0.5:
      return 'text-success-400';
    case deviation >= 0:
      return 'text-text-primary';
    case deviation >= -0.25:
      return 'text-text-primary';
    case deviation >= -0.5:
      return 'text-yellow-400';
    case deviation >= -1:
      return 'text-warning-400';
    default:
      return 'text-danger-400';
  }
}
</script>
